/**
 * "How it runs" as a choice of builder (utils/builderModel.js): every way the service can run that vfs-service found in
 * its folder, a radio each, with the command it runs on one line and "Recommended" on the one that runs when nothing
 * is chosen: the code stack (Node.js with a script picker, Python with its entry files, Go), the Makefile (a target
 * picker), each Dockerfile and compose file. A port field where the app may not take `PORT` (prefilled from the
 * files). "Use this builder" saves it in the service's `.flow/service.json`; "Restart with this builder" then offers
 * itself while the service runs (`offerRestart`). Without "Docker in the sandbox" the Docker ones are off, with the
 * plan's message and Change plan, or "Ask your Flow admin" where this Flow has no Docker support (NoDockerNote).
 * "Custom command" is always there: a command of its own (`make run`, `./start.sh`, `java -jar app.jar`), the port it
 * listens on when it does not take `PORT`, and optional install and build commands, saved as `start`, `port`, `install`
 * and `build` (the runner runs each with `sh -c` in the service folder). A hand-written `start` shows there, editable.
 * Suggestions under the command come from the folder (make targets, npm scripts, Python files). `setup`: opened from a
 * build that found no stack ("Set how it starts"): the recommended builder preselected when there is one, else Custom
 * command, and the save button says `saveLabel` ("Save and start"). Viewers see the choice, read-only. A line links
 * "What can Flow run?" (Supported languages and frameworks). A native device app ("iOS app (Swift)", "Android app
 * (Kotlin)", utils/deviceApps.js) has one choice, read-only: its "device" builder, which runs nothing in the sandbox
 * (Apps › Build for devices builds it); no Custom command, no port. "Use this builder" shows only while its
 * `.flow/service.json` says otherwise, and writes `{ "run": "device" }`.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, InputNumber, Radio, Select, message as toast } from 'antd';
import * as vfsClient from '../../utils/vfsClient';
import {
  builderById, builderSummary, commandLine, commandProblem, commandSuggestions, currentBuilder, MAX_COMMAND_LENGTH, optionOf,
  portFor, portProblem, specForBuilder, specForCustom, takesPort,
} from '../../utils/builderModel';
import { dockerOffMessage, rememberedDockerOff } from '../../utils/runnablesModel';
import { isDeviceBuilders } from '../../utils/deviceApps';
import NoDockerNote from './NoDockerNote';
import SupportedStacksLink from '../stacks/SupportedStacksLink';

const text = (value) => String(value || '').trim();
const sameChoice = (left, right) => left.id === right.id && (left.option || '') === (right.option || '') && (left.port || null) === (right.port || null)
  && (left.id !== 'custom' || (text(left.command) === text(right.command) && text(left.install) === text(right.install) && text(left.build) === text(right.build)));

/** The custom command of the service.json in use (empty when it runs another way). */
const customOf = (current) => (current.id === 'custom'
  ? { id: 'custom', option: '', port: current.port, command: current.command || '', install: current.install || '', build: current.build || '' }
  : { id: 'custom', option: '', port: null, command: '', install: '', build: '' });

/** What is chosen when the picker opens: what runs now; in `setup` (nothing could run) the recommended one, else custom. */
function firstDraft(current, info, { setup, noDocker }) {
  if (!setup || current.id === 'custom') { return current; }
  const recommended = builderById(info, info?.recommended);
  if (current.id && builderById(info, current.id) && !(builderById(info, current.id).docker && noDocker)) { return current; }
  if (recommended && !(recommended.docker && noDocker)) {
    return { id: recommended.id, option: info.option ?? recommended.option ?? '', port: portFor(recommended, info.option ?? recommended.option) };
  }
  return customOf(current);
}

export default function BuilderPicker({
  projectId, serviceName, info, spec = null, docker = null, dockerOff = undefined, readOnly = false, onSaved = null, offerRestart = false,
  onRestart = null, setup = false, saveLabel = 'Use this builder',
}) {
  const noDocker = docker === false;
  const offReason = dockerOff === undefined ? rememberedDockerOff(projectId) : dockerOff;
  const current = currentBuilder(spec, info);
  const [draft, setDraft] = useState(() => firstDraft(current, info, { setup, noDocker }));
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const currentKey = JSON.stringify(current);
  useEffect(() => { setDraft(firstDraft(JSON.parse(currentKey), info, { setup, noDocker })); setTouched(false); }, [currentKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const builders = info?.builders || [];
  const short = serviceName.replace(/^services\//, '');
  const suggestions = commandSuggestions(info);
  const pick = (id) => {
    if (id === 'custom') { setDraft(customOf(current)); return; }
    const builder = builderById(info, id);
    const option = id === current.id ? current.option : (id === info.recommended ? info.option : builder?.option) || '';
    setDraft({ id, option, port: id === current.id ? current.port : portFor(builder, option) });
  };
  const isCustom = draft.id === 'custom';
  const problems = isCustom ? {
    command: commandProblem(draft.command),
    install: commandProblem(draft.install, { optional: true }),
    build: commandProblem(draft.build, { optional: true }),
    port: portProblem(draft.port),
  } : {};
  const invalid = Object.values(problems).some(Boolean);
  const edit = (field) => (event) => { setTouched(true); setDraft({ ...draft, [field]: event.target.value }); };
  const pickOption = (builder, option) => setDraft({ id: builder.id, option, port: portFor(builder, option) ?? draft.port });

  const save = async(choice = draft) => {
    const custom = choice.id === 'custom';
    setSaving(true);
    try {
      const next = custom ? specForCustom(spec, choice) : specForBuilder(spec, choice, info);
      const path = `${serviceName}/.flow/service.json`;
      if (next) {
        await vfsClient.writeFile(projectId, path, `${JSON.stringify(next, null, 2)}\n`);
      } else if (spec) {
        await vfsClient.deleteEntry(projectId, path);
      }
      toast.success(`${short} runs with ${builderSummary(custom ? { id: 'custom', command: text(choice.command) } : choice, info)}`);
      onSaved?.(next);
    } catch (error) {
      toast.error(error.message || 'How it runs could not be saved');
    } finally {
      setSaving(false);
    }
  };

  // A native device app: its one builder, read-only (nothing to start, no port, no custom command).
  const device = isDeviceBuilders(info) ? (builders.find((builder) => builder.kind === 'device') || { id: 'device', kind: 'device', label: info.device?.label || 'Device app', command: '' }) : null;
  if (device) {
    return (
      <div className="apps-builders" data-testid="apps-builders">
        <div className="apps-builder-row" data-testid="apps-builder-device">
          <span className="apps-builder-name">
            {device.label}
            {device.file && <> ▸ <code>{device.file}</code></>}
            {current.id === 'device' ? <span className="apps-tag is-accent">In use</span> : <span className="apps-tag">Recommended</span>}
          </span>
          <code className="apps-builder-command" data-testid="apps-builder-command-device">
            {commandLine(device, '') || 'Nothing runs in the sandbox: build it for devices (Apps › Build for devices)'}
          </code>
        </div>
        <p className="apps-muted apps-small" data-testid="apps-builders-stacks">
          Which languages and frameworks Flow runs and checks: <SupportedStacksLink />
        </p>
        {!readOnly && current.id !== 'device' && (
          <div className="apps-builder-actions">
            <Button data-testid="apps-builder-save" loading={saving} onClick={() => save({ id: device.id, option: '', port: null })} type="primary">{saveLabel}</Button>
          </div>
        )}
      </div>
    );
  }

  // In setup nothing runs yet: what is preselected can be saved as it is.
  const dirty = setup || !sameChoice(draft, current);
  const inUse = current.id === 'custom';
  const customRow = (
    <div className="apps-builder-row" data-testid="apps-builder-custom" key="custom">
      <Radio className="apps-builder" disabled={readOnly || saving} value="custom">
        <span className="apps-builder-name">
          Custom command
          {inUse && <span className="apps-tag is-accent">In use</span>}
        </span>
        <code className="apps-builder-command" data-testid="apps-builder-command-custom">
          {inUse ? current.command : 'A command of its own: make run, ./start.sh, java -jar app.jar…'}
        </code>
        {readOnly && inUse && (current.install || current.build) && (
          <code className="apps-builder-command" data-testid="apps-builder-custom-steps">
            {[current.install && `install: ${current.install}`, current.build && `build: ${current.build}`].filter(Boolean).join(' · ')}
          </code>
        )}
      </Radio>
      {isCustom && !readOnly && (
        <div className="apps-builder-fields apps-builder-custom" data-testid="apps-builder-custom-fields">
          <label className="apps-builder-field">
            <span>Command</span>
            <Input
              aria-label="Command"
              autoFocus={setup}
              data-testid="apps-builder-custom-command"
              disabled={saving}
              maxLength={MAX_COMMAND_LENGTH + 1}
              onChange={edit('command')}
              placeholder="make run"
              status={touched && problems.command ? 'error' : undefined}
              value={draft.command}
            />
          </label>
          {touched && problems.command && <span className="apps-field-error" data-testid="apps-builder-custom-error" role="alert">{problems.command}</span>}
          {suggestions.length > 0 && (
            <span className="apps-builder-suggestions" data-testid="apps-builder-suggestions">
              <span className="apps-muted">From the folder:</span>
              {suggestions.map((command) => (
                <button className="apps-chip-button" disabled={saving} key={command} onClick={() => { setTouched(true); setDraft({ ...draft, command }); }} type="button">{command}</button>
              ))}
            </span>
          )}
          <label className="apps-builder-port">
            <span>Port</span>
            <InputNumber
              aria-label="Port"
              data-testid="apps-builder-port-custom"
              disabled={saving}
              max={65535}
              min={1}
              onChange={(value) => setDraft({ ...draft, port: value || null })}
              placeholder="PORT"
              value={draft.port}
            />
          </label>
          <p className="apps-muted apps-small apps-builder-hint">Your app gets PORT and must listen on it; or set the port it uses.</p>
          <label className="apps-builder-field">
            <span>Install <span className="apps-muted">(optional)</span></span>
            <Input aria-label="Install command" data-testid="apps-builder-custom-install" disabled={saving} maxLength={MAX_COMMAND_LENGTH + 1} onChange={edit('install')} placeholder="npm ci, pip install -r requirements.txt…" status={problems.install ? 'error' : undefined} value={draft.install} />
          </label>
          {problems.install && <span className="apps-field-error" role="alert">Install: {problems.install}</span>}
          <label className="apps-builder-field">
            <span>Build <span className="apps-muted">(optional)</span></span>
            <Input aria-label="Build command" data-testid="apps-builder-custom-build" disabled={saving} maxLength={MAX_COMMAND_LENGTH + 1} onChange={edit('build')} placeholder="make build" status={problems.build ? 'error' : undefined} value={draft.build} />
          </label>
          {problems.build && <span className="apps-field-error" role="alert">Build: {problems.build}</span>}
        </div>
      )}
    </div>
  );
  return (
    <div className="apps-builders" data-testid="apps-builders">
      <Radio.Group aria-label="How it runs" className="apps-builder-list" disabled={readOnly || saving} onChange={(event) => pick(event.target.value)} value={draft.id}>
        {inUse && customRow}
        {builders.map((builder) => {
          const on = draft.id === builder.id;
          const off = builder.docker && noDocker;
          const option = on ? draft.option : (builder.id === current.id ? current.option : builder.option);
          return (
            <div className="apps-builder-row" key={builder.id}>
              <Radio className="apps-builder" data-testid={`apps-builder-${builder.id}`} disabled={off || readOnly || saving} title={off ? dockerOffMessage(offReason) : undefined} value={builder.id}>
                <span className="apps-builder-name">
                  {builder.label}
                  {builder.file && (builder.kind === 'dockerfile' || builder.kind === 'compose' || builder.kind === 'make') && builder.file !== 'Dockerfile' && builder.file !== 'Makefile' && <> ▸ <code>{builder.file}</code></>}
                  {builder.id === info.recommended && <span className="apps-tag" data-testid={`apps-builder-recommended-${builder.id}`}>Recommended</span>}
                  {builder.id === current.id && <span className="apps-tag is-accent">In use</span>}
                </span>
                <code className="apps-builder-command" data-testid={`apps-builder-command-${builder.id}`}>{commandLine(builder, option)}</code>
              </Radio>
              {on && !readOnly && (builder.options.length > 1 || takesPort(builder, draft.option)) && (
                <div className="apps-builder-fields">
                  {builder.options.length > 1 && (
                    <Select
                      aria-label={builder.kind === 'make' ? 'Make target' : builder.kind === 'python' ? 'Python file' : 'Script'}
                      data-testid={`apps-builder-option-${builder.id}`}
                      disabled={saving}
                      onChange={(value) => pickOption(builder, value)}
                      options={builder.options.map((item) => ({ value: item.value, label: item.description ? `${item.label} — ${item.description}` : item.label }))}
                      popupMatchSelectWidth={false}
                      value={optionOf(builder, draft.option)?.value}
                    />
                  )}
                  {takesPort(builder, draft.option) && (
                    <label className="apps-builder-port">
                      <span>{builder.kind === 'dockerfile' ? 'Container port' : 'Port'}</span>
                      <InputNumber
                        aria-label="Port"
                        data-testid={`apps-builder-port-${builder.id}`}
                        disabled={saving}
                        max={65535}
                        min={1}
                        onChange={(value) => setDraft({ ...draft, port: value || null })}
                        placeholder={builder.kind === 'dockerfile' ? 'EXPOSE' : 'PORT'}
                        value={draft.port}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!inUse && customRow}
      </Radio.Group>
      {!readOnly && takesPort(builderById(info, draft.id), draft.option) && builderById(info, draft.id)?.kind !== 'dockerfile' && (
        <p className="apps-muted apps-small">It gets PORT; set a port only when it listens on a fixed one.</p>
      )}
      {noDocker && builders.some((builder) => builder.docker) && (
        <p className="apps-muted apps-small" data-testid="apps-builders-no-docker"><NoDockerNote reason={offReason} /></p>
      )}
      <p className="apps-muted apps-small" data-testid="apps-builders-stacks">
        Which languages and frameworks Flow runs and checks: <SupportedStacksLink />
      </p>
      {!readOnly && (
        <div className="apps-builder-actions">
          <Button data-testid="apps-builder-save" disabled={!dirty || !draft.id || invalid} loading={saving} onClick={() => save()} type="primary">{saveLabel}</Button>
          {offerRestart && !dirty && onRestart && (
            <Button data-testid="apps-builder-restart" onClick={onRestart}>Restart with this builder</Button>
          )}
        </div>
      )}
    </div>
  );
}

BuilderPicker.propTypes = {
  docker: PropTypes.bool,
  dockerOff: PropTypes.oneOf(['cluster', 'plan', null]),
  // vfs-service's builders of this service: { builders, recommended, option, stack, composeApp }.
  info: PropTypes.object,
  // Shown after a save while the service runs.
  offerRestart: PropTypes.bool,
  onRestart: PropTypes.func,
  // (nextSpec) => after the choice was written (null: the file was removed).
  onSaved: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  // The save button's words ("Save and start" in setup).
  saveLabel: PropTypes.string,
  serviceName: PropTypes.string.isRequired,
  // Opened because nothing could run ("Set how it starts"): the recommended builder, else Custom command, preselected.
  setup: PropTypes.bool,
  spec: PropTypes.object,
};
